import { useMemo } from "react";
import type { DailyRecordRow } from "@/lib/database.types";
import { SYMPTOMS, type SymptomKey } from "./DailyCheckinCard";

const DAYS = 14;

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * One row per symptom: the last 14 days as tiny bars (height = severity/5),
 * latest value called out on the right. Efficacy symptoms trend UP as the
 * medication works; tolerability symptoms should trend DOWN after titration —
 * both are legible at a glance here.
 */
export function SymptomTrends({ records }: { records: DailyRecordRow[] }) {
  const days = useMemo(() => {
    const byDate = new Map(records.map((r) => [r.record_date, r]));
    const out: Array<{ date: string; record: DailyRecordRow | null }> = [];
    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = localISO(d);
      out.push({ date: iso, record: byDate.get(iso) ?? null });
    }
    return out;
  }, [records]);

  const hasAny = records.some((r) =>
    SYMPTOMS.some((s) => r[s.key as SymptomKey] != null),
  );

  if (!hasAny) {
    return (
      <p className="text-[13px] text-fh-gray m-0">
        Rate your symptoms in a daily check-in and your two-week trends will appear here.
      </p>
    );
  }

  return (
    <div className="space-y-3.5">
      {SYMPTOMS.map((s) => {
        const latest = [...records]
          .sort((a, b) => b.record_date.localeCompare(a.record_date))
          .find((r) => r[s.key] != null);
        return (
          <div key={s.key} className="flex items-center gap-3">
            <div className="w-[168px] shrink-0 text-[13px] font-semibold text-fh-ink leading-tight">
              {s.label}
            </div>
            <div className="flex items-end gap-[3px] h-8 flex-1" aria-hidden="true">
              {days.map(({ date, record }) => {
                const v = record?.[s.key] ?? null;
                return (
                  <div
                    key={date}
                    title={v != null ? `${date}: ${v}/5` : date}
                    className={[
                      "flex-1 max-w-[14px] rounded-t-[3px]",
                      v == null
                        ? "bg-fh-sand h-[3px]"
                        : v === 0
                          ? "bg-fh-green-light/50 h-[4px]"
                          : "bg-fh-green",
                    ].join(" ")}
                    style={v != null && v > 0 ? { height: `${(v / 5) * 100}%` } : undefined}
                  />
                );
              })}
            </div>
            <div className="w-9 shrink-0 text-right text-[12.5px] font-semibold text-fh-green-dark">
              {latest?.[s.key] != null ? `${latest[s.key]}/5` : "—"}
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-fh-gray-light m-0 pt-1">
        Last {DAYS} days · taller bars mean stronger symptoms.
      </p>
    </div>
  );
}
